import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from './AppContext';
import { LOGO_URL } from '../lib/config';
import { changePassword } from '../lib/api';

type Toast = (text: string, kind?: 'ok' | 'err' | 'info') => void;

function ChangePasswordModal({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 8) { toast('New password must be at least 8 characters', 'err'); return; }
    if (next !== confirm) { toast('New passwords don’t match', 'err'); return; }
    setBusy(true);
    try { await changePassword(current, next); toast('Password changed', 'ok'); onClose(); }
    catch (e2) { toast(e2 instanceof Error ? e2.message : 'Could not change password', 'err'); setBusy(false); }
  }
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Change password</h3>
        <p className="page-sub" style={{ marginTop: 0 }}>For email &amp; password accounts. (Google sign-in users manage their password with Google.)</p>
        <form className="stack" onSubmit={save}>
          <label className="field">Current password<input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required /></label>
          <label className="field">New password<input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required /></label>
          <label className="field">Confirm new password<input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></label>
          <div className="row">
            <button type="button" className="btn secondary" onClick={onClose}>Cancel</button>
            <button className="btn" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ADMIN_ITEMS: { to: string; label: string }[] = [
  { to: '/admin/catalog', label: 'Catalog' },
  { to: '/admin/media', label: 'Media library' },
  { to: '/admin/allocation', label: 'Allocation' },
  { to: '/admin/reports', label: 'Reports' },
  { to: '/admin/people', label: 'People' },
  { to: '/admin/settings', label: 'Settings' },
];

function AdminMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const onAdmin = location.pathname.startsWith('/admin');

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Close the popup whenever the route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="admin-menu" ref={ref}>
      <button
        type="button"
        className={`admin-trigger${onAdmin ? ' active' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Admin <span className="caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="admin-pop" role="menu">
          {ADMIN_ITEMS.map((it) => (
            <button
              key={it.to}
              type="button"
              role="menuitem"
              className={`admin-pop-item${location.pathname === it.to ? ' active' : ''}`}
              onClick={() => { setOpen(false); navigate(it.to); }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { me, isAdmin, readyCount, signOut, toast, toasts } = useApp();
  const [logoOk, setLogoOk] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            {logoOk
              ? <img src={LOGO_URL} alt="Stray Cat Blues" onError={() => setLogoOk(false)} />
              : <span className="fallback">🐾 Stray Cat Blues</span>}
          </div>
          <nav className="nav">
            <NavLink to="/" end>Shop</NavLink>
            <NavLink to="/my" className={({ isActive }) => `nav-my${isActive ? ' active' : ''}`}>
              My Items
              {readyCount > 0 && (
                <span className="nav-badge" title={`${readyCount} ready to pick up`}>{readyCount}</span>
              )}
            </NavLink>
            {isAdmin && <AdminMenu />}
          </nav>
          <div className="spacer" />
          <div className="who">
            <b>{me?.display_name || me?.email}</b>
            <span>{me?.role.replace('_', '-')} · <button className="btn-link" onClick={() => setPwOpen(true)}>Change password</button> · <button className="btn-link" onClick={signOut}>Sign out</button></span>
          </div>
        </div>
      </header>
      <main className="main">{children}</main>
      <div className="toast-wrap">
        {toasts.map((t) => <div key={t.id} className={`toast ${t.kind}`}>{t.text}</div>)}
      </div>
      {pwOpen && <ChangePasswordModal toast={toast} onClose={() => setPwOpen(false)} />}
    </div>
  );
}
